import type { MasterBus } from "../effects/MasterBus";
import type { ControlStore } from "../state/controlStore";
import type { UserControls } from "../state/types";

/**
 * Owns route 1 (immediate) and route 2 (audio-rate) from
 * docs/implementation-plan.md ch.4.
 *
 * It subscribes to the ControlStore so these parameters are applied the instant
 * the user acts — it never waits for a bar boundary. Musical parameters (BPM,
 * and in Phase 1 genre/density/harmony) are deliberately NOT handled here: the
 * Music Control Layer pulls those at bar boundaries.
 *
 * In Phase 0 only master volume and mute exist. Phase 1 adds filter, reverb,
 * delay, pan and FX send with 50-500ms ramps.
 */
export class AudioParameterController {
  private unsubscribe: (() => void) | null = null;
  private lastVolume: number;
  private lastMuted: boolean;

  constructor(
    private readonly store: ControlStore,
    private readonly master: MasterBus,
  ) {
    const initial = store.getSnapshot();
    this.lastVolume = initial.sound.masterVolume;
    this.lastMuted = initial.sound.muted;
  }

  start(): void {
    if (this.unsubscribe !== null) return;
    this.unsubscribe = this.store.subscribe(() => {
      this.apply(this.store.getSnapshot());
    });
  }

  /** Applies the current values regardless of change detection. */
  applyCurrent(): void {
    this.apply(this.store.getSnapshot(), true);
  }

  private apply(controls: UserControls, force = false): void {
    const { masterVolume, muted } = controls.sound;

    if (force || muted !== this.lastMuted) {
      this.lastMuted = muted;
      if (muted) {
        this.master.mute("muted");
      } else {
        this.master.applyVolumeIfAudible(masterVolume);
      }
    }

    if (force || masterVolume !== this.lastVolume) {
      this.lastVolume = masterVolume;
      if (!muted) {
        // No-op while paused/panicked: the UI value is kept and applied on resume.
        this.master.applyVolumeIfAudible(masterVolume);
      }
    }
  }

  stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }
}
